import {
  canAccessAdminWorkspace,
  canAccessFleetSuperAdminControls,
  canAccessOrchardWorkspace,
  canAccessOpsMonitoringWorkspace,
  canAccessStockOpsWorkspace,
  getOperatorSessionScopeLabel,
  isFleetOperatorSession,
  isOrchardOperatorSession,
  titleizeToken,
  type OperatorResolvedSession,
} from "@/features/operator/shared/operator-access"
import type {
  OperatorShellBreadcrumb,
} from "@/features/operator/shared/operator-app-shell"
import type {
  OperatorShellIdentitySummary,
} from "@/features/operator/shared/operator-app-sidebar"
import {
  operatorPrimaryNavigationScaffold,
  operatorSecondaryNavigationScaffold,
  resolveOperatorPrimaryNavigation,
  resolveOperatorSecondaryNavigation,
  type OperatorShellNavigationItemState,
  type OperatorShellSecondaryNavigationItemState,
} from "@/features/operator/shared/operator-shell-navigation"

export interface OperatorShellFrameState {
  brandHref: string
  brandName: string
  brandTagline: string
  breadcrumbs: readonly OperatorShellBreadcrumb[]
  headerDescription: string
  headerTitle: string
  navigationGroupLabel: string
  navigation: readonly OperatorShellNavigationItemState[]
  secondaryNavigation: readonly OperatorShellSecondaryNavigationItemState[]
  user: OperatorShellIdentitySummary
}

function isOrchardShellPath(pathname: string) {
  return pathname === "/orchards" || pathname.startsWith("/orchards/")
}

function isFleetShellPath(pathname: string) {
  return pathname === "/fleet" || pathname.startsWith("/fleet/")
}

function isAuthorityShellPath(pathname: string) {
  return pathname === "/authority" || pathname.startsWith("/authority/")
}

function canAccessExportsWorkspace(session: OperatorResolvedSession) {
  if (isOrchardOperatorSession(session) || isFleetOperatorSession(session)) {
    return false
  }

  return Boolean(session.permissions.manageExports || session.permissions.exportEvidence)
}

function canAccessFnolWorkspace(session: OperatorResolvedSession) {
  return canAccessExportsWorkspace(session)
}

function canAccessOpsIntegrationsWorkspace(session: OperatorResolvedSession) {
  if (isFleetOperatorSession(session)) {
    return false
  }

  return Boolean(
    session.permissions.manageExports
      || session.permissions.viewAnalytics
      || canAccessOpsMonitoringWorkspace(session)
  )
}

function canAccessPartnerDeliveriesWorkspace(session: OperatorResolvedSession) {
  if (isFleetOperatorSession(session)) {
    return false
  }

  return Boolean(session.permissions.manageExports || session.permissions.viewAnalytics)
}

function canViewNavigationItem(
  label: string,
  session: OperatorResolvedSession,
  pathname: string
) {
  if (isOrchardOperatorSession(session) || isOrchardShellPath(pathname)) {
    return label === "Orchards"
  }

  if (isFleetOperatorSession(session)) {
    return label === "Fleet"
  }

  switch (label) {
    case "Orchards":
      return canAccessOrchardWorkspace(session)
    case "Admin":
      return canAccessAdminWorkspace(session)
    case "Ops":
      return (
        canAccessOpsMonitoringWorkspace(session)
        || canAccessOpsIntegrationsWorkspace(session)
        || canAccessPartnerDeliveriesWorkspace(session)
      )
    case "Exports":
      return canAccessExportsWorkspace(session)
    default:
      return true
  }
}

function canViewNavigationChild(
  parentLabel: string,
  label: string,
  session: OperatorResolvedSession,
  pathname: string
) {
  switch (`${parentLabel}:${label}`) {
    case "Fleet:Authority Directory":
      return canAccessFleetSuperAdminControls(session)
    case "Orchards:Overview":
      return canAccessOrchardWorkspace(session)
    case "Admin:Stock Ops":
      return canAccessStockOpsWorkspace(session)
    case "Ops:Monitor":
      return canAccessOpsMonitoringWorkspace(session)
    case "Ops:Integrations":
      return canAccessOpsIntegrationsWorkspace(session)
    case "Ops:Partner Deliveries":
      return canAccessPartnerDeliveriesWorkspace(session)
    case "Exports:FNOL":
      return canAccessFnolWorkspace(session)
    default:
      return canViewNavigationItem(parentLabel, session, pathname)
  }
}

function resolveShellIdentity(
  session: OperatorResolvedSession
): OperatorShellIdentitySummary {
  return {
    displayName: session.displayName,
    roleLabel: titleizeToken(session.roleToken),
    scopeLabel: getOperatorSessionScopeLabel(session),
  }
}

function resolveShellBranding(
  pathname: string,
  session: OperatorResolvedSession
) {
  if (isFleetOperatorSession(session)) {
    return {
      brandHref: "/fleet",
      brandName: "Fleet Safety",
      brandTagline: "Fleet operations",
      navigationGroupLabel: "Fleet workspace",
    }
  }

  if (isOrchardOperatorSession(session) || isOrchardShellPath(pathname)) {
    return {
      brandHref: "/orchards",
      brandName: "GrowBetter",
      brandTagline: "Orchard Intelligence",
      navigationGroupLabel: "Orchard workspace",
    }
  }

  return {
    brandHref: "/",
    brandName: "Traffic Ops",
    brandTagline: "Operator workspace",
    navigationGroupLabel: "Operator surfaces",
  }
}

export function resolveOperatorShellNavigation(
  pathname: string,
  session: OperatorResolvedSession
): readonly OperatorShellNavigationItemState[] {
  const navigation = resolveOperatorPrimaryNavigation(
    pathname,
    operatorPrimaryNavigationScaffold
  )

  if (isOrchardShellPath(pathname)) {
    return navigation
      .filter((item) => item.label === "Orchards")
      .map((item) => ({
        ...item,
        items: item.items?.filter((child) => child.label === "Overview"),
      }))
  }

  if (isFleetOperatorSession(session)) {
    return navigation
      .filter((item) => item.label === "Fleet")
      .map((item) => ({
        ...item,
        items: item.items?.filter((child) => {
          return (
            child.isActive
            || canViewNavigationChild(item.label, child.label, session, pathname)
          )
        }),
      }))
  }

  return navigation.flatMap((item) => {
    const visibleChildren = item.items?.filter((child) => {
      return (
        child.isActive
        || canViewNavigationChild(item.label, child.label, session, pathname)
      )
    })
    const visible =
      item.isActive
      || item.hasActiveChild
      || canViewNavigationItem(item.label, session, pathname)
      || Boolean(visibleChildren?.length)

    if (!visible) {
      return []
    }

    return [
      {
        ...item,
        hasActiveChild: visibleChildren?.some((child) => child.isActive) ?? false,
        items: visibleChildren,
      },
    ]
  })
}

function resolveShellBreadcrumbs(
  navigation: readonly OperatorShellNavigationItemState[]
): readonly OperatorShellBreadcrumb[] {
  const activeItem =
    navigation.find((item) => item.hasActiveChild) ??
    navigation.find((item) => item.isActive)
  const activeChild = activeItem?.items?.find((item) => item.isActive) ?? null

  if (!activeItem) {
    return []
  }

  if (!activeChild || activeChild.to === activeItem.to) {
    return [{ label: activeItem.label }]
  }

  return [
    {
      label: activeItem.label,
      to: activeItem.to,
    },
    {
      label: activeChild.label,
    },
  ]
}

function buildOperatorShellFrameState(
  navigation: readonly OperatorShellNavigationItemState[],
  pathname: string,
  session: OperatorResolvedSession,
  user: OperatorShellIdentitySummary
): OperatorShellFrameState {
  const branding = resolveShellBranding(pathname, session)
  const secondaryNavigation = resolveOperatorSecondaryNavigation(
    pathname,
    operatorSecondaryNavigationScaffold
  )
  const activeItem =
    navigation.find((item) => item.hasActiveChild) ??
    navigation.find((item) => item.isActive) ??
    navigation[0]
  const activeChild = activeItem?.items?.find((item) => item.isActive) ?? null

  return {
    ...branding,
    breadcrumbs: resolveShellBreadcrumbs(navigation),
    headerDescription:
      activeChild?.description
      || activeItem?.description
      || (
        isOrchardOperatorSession(session) || isOrchardShellPath(pathname)
          ? "Manage orchards, tree registry, QR tags, and seasonal field observations."
          : isFleetOperatorSession(session)
            ? "Manage fleet onboarding, operational masters, sticker issuance, and certification readiness."
            : "Manage incidents, fleets, operations, and export workflows."
      ),
    headerTitle:
      activeChild?.label
      || activeItem?.label
      || ((isOrchardOperatorSession(session) || isOrchardShellPath(pathname))
        ? "Orchard workspace"
        : isFleetOperatorSession(session)
          ? "Fleet workspace"
        : "Operator workspace"),
    navigation,
    secondaryNavigation,
    user,
  }
}

export function resolveOperatorShellFrameState(
  pathname: string,
  session: OperatorResolvedSession
): OperatorShellFrameState {
  return buildOperatorShellFrameState(
    resolveOperatorShellNavigation(pathname, session),
    pathname,
    session,
    resolveShellIdentity(session)
  )
}

export function resolveOperatorShellFallbackFrameState(
  pathname: string,
  session: OperatorResolvedSession,
  user: OperatorShellIdentitySummary
): OperatorShellFrameState {
  return buildOperatorShellFrameState(
    resolveOperatorShellNavigation(pathname, session),
    pathname,
    session,
    user
  )
}

export function resolveOperatorShellLaneRedirect(
  pathname: string,
  session: OperatorResolvedSession
) {
  if (isFleetOperatorSession(session) && !isFleetShellPath(pathname)) {
    if (canAccessFleetSuperAdminControls(session) && isAuthorityShellPath(pathname)) {
      return "/fleet/authority-directory"
    }

    return "/fleet"
  }

  return null
}
